import { Button, Dialog, DialogActions, TextArea } from '@wodi/ui';
import { useState, type ReactNode } from 'react';

/** Matches `reasonSchema` in @wodi/shared: 5 to 500 characters. */
export const REASON_MIN_LENGTH = 5;

/**
 * Asks for the reason before a correction that can't be undone, like marking a record
 * entered in error (CLAUDE.md: nothing clinical is deleted; the reason is stored and
 * audited). The action stays disabled in effect until the reason is long enough.
 */
export function ReasonDialog({
  title,
  isOpen,
  onOpenChange,
  confirmLabel,
  pendingLabel,
  isPending,
  error,
  onConfirm,
  children,
}: {
  title: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  confirmLabel: string;
  pendingLabel: string;
  isPending: boolean;
  /** A failure from the server, in words. */
  error: string | null;
  onConfirm: (reason: string) => void;
  children?: ReactNode;
}) {
  const [reason, setReason] = useState('');
  const [tried, setTried] = useState(false);
  const short = reason.trim().length < REASON_MIN_LENGTH;
  return (
    <Dialog title={title} isOpen={isOpen} onOpenChange={onOpenChange}>
      {children}
      <TextArea
        label="Reason"
        description="Stored with the record and shown in its history."
        value={reason}
        onChange={setReason}
        {...(tried && short
          ? {
              errorMessage: `Give a reason of at least ${String(REASON_MIN_LENGTH)} characters.`,
            }
          : error === null
            ? {}
            : { errorMessage: error })}
      />
      <DialogActions>
        <Button
          variant="quiet"
          onPress={() => {
            onOpenChange(false);
          }}
        >
          Keep it
        </Button>
        <Button
          variant="primary"
          isPending={isPending}
          pendingLabel={pendingLabel}
          onPress={() => {
            setTried(true);
            if (!short) onConfirm(reason.trim());
          }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
